import React, { useState, useEffect } from 'react'
import { generatePath } from 'react-router'
import { routes } from '../../../routes'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Divider, Grid, Switch, Typography, Link, useTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { BannerStatic, TemplateSelect, TemplatePromotedContent } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from '../features-tab/style'
import { useTenantFormContext } from '../tenant-form-context'
import { TOOLKIT_TYPE, CONSTANTS } from '../../../util/constants'
import { Controller } from 'react-hook-form'
import ContentHubConfirmationModal from '../components/content-hub-confirmation'

const TenantTemplate = ({ template, templateHasChosenOnEdit }) => {
  const { spacing } = useTheme()
  const [isChangingTemplate, setIsChangingTemplate] = React.useState(false)
  const { form: { watch, setValue, getValues }, isEdit } = useTenantFormContext()
  const templateName = getValues('templateName')
  const [baseTemplate, templateSK] = watch(['baseTemplate', 'templateSK'])

  const handleTemplateSelect = React.useCallback((template) => {
    setValue('baseTemplate', template.sk)
    setValue('templateName', template.name)
    setIsChangingTemplate(false)
  }, [setValue])

  const handleTemplateChange = React.useCallback(() => {
    setIsChangingTemplate(true)
    setValue('baseTemplate', null)
  }, [setValue, setIsChangingTemplate])

  if ((!baseTemplate && !templateHasChosenOnEdit) || isChangingTemplate) {
    return <TemplateSelect onTemplateSelect={handleTemplateSelect} />
  }

  return (
    <Box display='flex' flexDirection='row' sx={{ mt: spacing(3.5) }} gap={1} data-test='content-hub-selected-template-container'>
      <Typography variant='body2'>
        {isEdit && templateSK ? 'Tenant Template:' : 'Selected Base Template:'}{' '}
        <strong>{templateName || template?.name}</strong>
      </Typography>
      <Typography variant='body2'>
        <Link
          href={isEdit && templateSK ? generatePath(routes.templateEdit.path, { id: template?.sk }) : undefined}
          onClick={!isEdit || !templateSK ? handleTemplateChange : undefined}
          sx={{ cursor: 'pointer' }}
          data-test={isEdit && templateSK ? 'content-hub-view-template-link' : 'content-hub-change-template-link'}
        >
          {isEdit && templateSK ? 'View' : 'Change'}
        </Link>
      </Typography>
    </Box>
  )
}

const ContentHubSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { form, isEdit, tenant } = useTenantFormContext()
  const { watch, setValue } = form
  const [toolkitType, contentHub, baseTemplate, templateSK] = watch(['toolkitType', 'features.contentHub', 'baseTemplate', 'templateSK'])
  const isClub = (isEdit && tenant.pk === CONSTANTS.DEFAULT_CLUB_PK)
  const isSBT = toolkitType === TOOLKIT_TYPE.smallBusinessToolkit
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [templateHasChosenOnEdit, setTemplateHasChosenOnEdit] = React.useState(false)

  const template = useSelector(TenantEditSlice.selectors.tenantTemplate)
  const setTemplate = React.useCallback((template) => {
    dispatch(TenantEditSlice.actions.updateTenantTemplate(template))
  }, [dispatch])

  const handleSwitch = () => {
    setValue('features.contentHub', !contentHub)
  }

  useEffect(() => {
    if (!isEdit || isSBT) {
      setValue('features.contentHub', true)
      return
    }

    const hasContentHubField = Object.hasOwn(tenant.features, 'contentHub')
    const contentHub = tenant?.features?.contentHub
    const hasContentHub = hasContentHubField ? contentHub : true
    setValue('features.contentHub', hasContentHub)
  }, [setValue, tenant, isEdit, isSBT, template])

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Content Hub configuration updated' })
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
        if (!baseTemplate) {
          dispatch(TenantEditSlice.actions.updateTenantTemplate(tenant.tenantTemplate))
          setTemplateHasChosenOnEdit(true)
          setValue('baseTemplate', tenant.tenantTemplate.pathToRoot)
          setValue('templateSK', tenant.tenantTemplate.sk)
          setValue('templateName', tenant.tenantTemplate.name)
        }
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Content Hub feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handlePromotedContentModeChange = React.useCallback((mode) => {
    setTemplate({ ...template, promotedContentMode: mode })
  }, [setTemplate, template])

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      name: tenant.name,
      baseTemplate,
      features: {
        ...tenant.features,
        contentHub: contentHub
      }
    })
    setIsLoading(true)
  }

  const handleShowConfirmationModal = () => {
    if (templateSK) {
      handleSave()
      return
    }

    setOpen(true)
  }

  const isDisabled = isLoading || !baseTemplate

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Box>
              <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
                Content Hub
              </Typography>
              <Box sx={{ marginY: 3 }} />
              {contentHub && (
                <TenantTemplate
                  template={template}
                  templateHasChosenOnEdit={templateHasChosenOnEdit}
                />
              )}
              <Box sx={{ marginY: 3 }} />
              {contentHub && template && isEdit && (tenant?.templateSK || templateHasChosenOnEdit) && (
                <Box>
                  <Divider style={{ marginBottom: spacing(2) }} />
                  <TemplatePromotedContent
                    template={template}
                    onPromotedContentModeChanged={handlePromotedContentModeChange}
                  />
                </Box>
              )}
            </Box>
            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Controller
                  name='features.contentHub'
                  control={form.control}
                  render={({ field }) =>
                    <Switch
                      color='primary'
                      checked={!!field.value}
                      onChange={handleSwitch}
                      disabled={isLoading || isClub || isSBT}
                      data-test='content-hub-product-toggle'
                      {...field}
                    />}
                />
              </Box>
            </Box>
          </Box>
          {(isEdit && !isSBT && !isClub) && (
            <Box>
              <Divider style={{ marginBottom: spacing(1) }} />
              <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
                <ActionButton
                  onClick={handleShowConfirmationModal}
                  disabled={isDisabled}
                  variant='primary'
                  dataTest='content-hub-save-button'
                  style={{ height: 'min-content' }}
                > Save
                </ActionButton>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
      <ContentHubConfirmationModal open={open} onClose={() => setOpen(false)} handleConfirmation={handleSave} />
    </Grid>
  )
}

export default ContentHubSettings
